import { useTranslation } from 'react-i18next';
import { formatPercent } from '~/utils';

type MarketPricePillProps = {
  price: string;
  label?: string;
  percentFromMarket?: number | null;
};

export function MarketPricePill({
  price,
  label,
  percentFromMarket,
}: Readonly<MarketPricePillProps>) {
  const { t } = useTranslation('general');

  return (
    <span className="px-2 py-0.5 text-xs bg-status-label-100 text-status-label-700 rounded font-medium whitespace-nowrap">
      {label ?? t('market_price_pill.label')} {price}
      {percentFromMarket != null && (
        <>
          {' · '}
          <PercentFromMarket percent={percentFromMarket} />
        </>
      )}
    </span>
  );
}

function PercentFromMarket({ percent }: Readonly<{ percent: number }>) {
  const { t } = useTranslation('general');

  if (percent === 0) {
    return (
      <span data-testid="market-price-percent">
        {t('market_price_pill.at_market')}
      </span>
    );
  }

  const isAbove = percent > 0;
  const magnitude = formatPercent(Math.abs(percent) / 100);
  return (
    <span
      data-testid="market-price-percent"
      className={isAbove ? 'text-status-error' : 'text-status-success'}
    >
      <span aria-hidden="true">
        {isAbove ? '▲' : '▼'} {magnitude}
      </span>
      <span className="sr-only">
        {t(
          isAbove
            ? 'market_price_pill.above_market'
            : 'market_price_pill.below_market',
          { percent: magnitude }
        )}
      </span>
    </span>
  );
}
